/**
 * OAuth callback handler for Google and Okta authentication
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../../services/authApi';
import './OAuthCallback.css';

const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const errorParam = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        // Check for OAuth errors
        if (errorParam) {
          const errorMessage = errorDescription || `OAuth error: ${errorParam}`;
          setError(errorMessage);
          setStatus('error');
          
          // Redirect to login with error after delay
          setTimeout(() => {
            navigate(`/login?error=${encodeURIComponent(errorMessage)}`);
          }, 3000);
          return;
        }

        // Verify required parameters
        if (!code) {
          setError('Missing authorization code');
          setStatus('error');
          setTimeout(() => navigate('/login?error=Missing authorization code'), 3000);
          return;
        }

        // Get stored state and verify
        const storedState = sessionStorage.getItem('auth_state');
        const provider = sessionStorage.getItem('auth_provider') as 'google' | 'microsoft' | 'okta';
        // const email = sessionStorage.getItem('auth_email');
        const emailDomain = sessionStorage.getItem('auth_email_domain');

        if (state !== storedState) {
          setError('Invalid state parameter - possible CSRF attack');
          setStatus('error');
          setTimeout(() => navigate('/login?error=Security error - please try again'), 3000);
          return;
        }

        if (!provider) {
          setError('Missing provider information');
          setStatus('error');
          setTimeout(() => navigate('/login?error=Session expired - please try again'), 3000);
          return;
        }

        // Complete OAuth login
        const redirectUri = `${window.location.origin}/auth/callback`;
        const loginRequest = {
          provider,
          code,
          redirect_uri: redirectUri,
          state: state || undefined,
          email_domain: emailDomain || undefined
        };

        console.log('🔍 OAuth callback - completing login with:', loginRequest);
        const tokenResponse = await authApi.completeOAuthLogin(loginRequest);
        console.log('✅ OAuth callback - token response:', tokenResponse);
        
        // Store authentication data
        authApi.storeAuthData(tokenResponse);
        
        setStatus('success');
        
        // Clean up session storage
        sessionStorage.removeItem('auth_state');
        sessionStorage.removeItem('auth_provider');
        sessionStorage.removeItem('auth_email');
        sessionStorage.removeItem('auth_email_domain');
        sessionStorage.removeItem('auth_organization');
        
        // Force full page reload to ensure AuthContext picks up the new auth state
        // This is necessary because the AuthContext only checks localStorage on mount
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);

      } catch (err: any) {
        console.error('❌ OAuth callback failed:', err);
        console.error('❌ Full error object:', JSON.stringify(err, null, 2));
        console.error('❌ Error response:', err.response);
        console.error('❌ Error response data:', err.response?.data);
        
        const errorMessage = err.response?.data?.detail || err.message || 'Authentication failed';
        console.error('❌ Final error message:', errorMessage);
        
        setError(errorMessage);
        setStatus('error');
        
        // Redirect to login with error
        setTimeout(() => {
          navigate(`/login?error=${encodeURIComponent(errorMessage)}`);
        }, 3000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="oauth-callback-container">
      <div className="oauth-callback-content">
        {status === 'processing' && (
          <>
            <div className="callback-spinner">
              <div className="spinner"></div>
            </div>
            <h2>Completing authentication...</h2>
            <p>Please wait while we sign you in.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="success-icon">✓</div>
            <h2>Authentication successful!</h2>
            <p>Redirecting to your dashboard...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="error-icon">⚠️</div>
            <h2>Authentication failed</h2>
            <p className="error-details">{error}</p>
            <p className="redirect-message">
              Redirecting to login page in a few seconds...
            </p>
            <button 
              onClick={() => navigate('/login')}
              className="back-to-login-btn"
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
